import type { ReactNode } from 'react';
import { isApiEntity, type Entity } from '@backstage/catalog-model';
import { isComponentType } from '@backstage/plugin-catalog';
import { EmptyState } from '@backstage/core-components';
import { compatWrapper } from '@backstage/core-compat-api';
import { coreExtensionData, type FrontendPlugin } from '@backstage/frontend-plugin-api';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { githubViewPermission } from '@internal/backstage-plugin-github-common';
import nativeApiDocsPlugin from '@backstage/plugin-api-docs/alpha';
import nativeCatalogGraphPlugin from '@backstage/plugin-catalog-graph/alpha';
import nativeGithubActionsPlugin from '@backstage-community/plugin-github-actions/alpha';
import nativeGithubPullRequestsPlugin from '@roadiehq/backstage-plugin-github-pull-requests/alpha';
import nativeCodeInsightsPlugin from '@roadiehq/backstage-plugin-github-insights/alpha';
import { isGithubActionsAvailable } from '@backstage-community/plugin-github-actions';
import { isGithubPullRequestsAvailable } from '@roadiehq/backstage-plugin-github-pull-requests';
import { isGithubInsightsAvailable } from '@roadiehq/backstage-plugin-github-insights';
import { isApiEntityMcpServer } from '../../components/catalog/mcp/isApiEntityMcpServer';
import { isFullComponent } from './entityPredicates';
import styles from './catalogGraphPage.module.css';

const WithGitHubPermission = ({ children }: { children: ReactNode }) => (
  <RequirePermission
    permission={githubViewPermission}
    errorPage={
      <EmptyState
        missing="info"
        title="Restricted"
        description="You need GitHub access to view this content."
      />
    }
  >
    {children}
  </RequirePermission>
);

// The API tabs sit between the GitHub tabs and Docs instead of in native tab groups.
export const nfsApiDocsPlugin = nativeApiDocsPlugin.withOverrides({
  extensions: [
    nativeApiDocsPlugin.getExtension('entity-content:api-docs/apis').override({
      factory: (originalFactory, { config }) =>
        originalFactory({
          config: { ...config, group: config.group ?? false },
          params: { path: '/api', title: 'API', filter: isComponentType('service') },
        }),
    }),
    nativeApiDocsPlugin.getExtension('entity-content:api-docs/definition').override({
      factory: (originalFactory, { config }) =>
        originalFactory({
          config: { ...config, group: config.group ?? false },
          params: {
            filter: (entity: Entity) => isApiEntity(entity) && !isApiEntityMcpServer(entity),
          },
        }),
    }),
    // These are on the API tab, or the Definition tab.
    ...(
      [
        'entity-card:api-docs/has-apis',
        'entity-card:api-docs/definition',
        'entity-card:api-docs/provided-apis',
        'entity-card:api-docs/consumed-apis',
      ] as const
    ).map(id => nativeApiDocsPlugin.getExtension(id).override({ disabled: true })),
    // Varia's own API explorer page serves /api-docs.
    nativeApiDocsPlugin.getExtension('page:api-docs').override({ disabled: true }),
  ],
});

export const nfsCatalogGraphPlugin = nativeCatalogGraphPlugin.withOverrides({
  extensions: [
    nativeCatalogGraphPlugin.getExtension('page:catalog-graph').override({
      factory: originalFactory => {
        const original = originalFactory();
        const page = original.get(coreExtensionData.reactElement);
        return [...original].map(output =>
          output.id === coreExtensionData.reactElement.id
            ? coreExtensionData.reactElement(
                <div className={styles.withoutLegacyHeader}>{page}</div>,
              )
            : output,
        );
      },
    }),
    nativeCatalogGraphPlugin.getExtension('entity-card:catalog-graph/relations').override({
      params: { filter: { $not: { kind: { $in: ['user', 'group', 'template'] } } } },
    }),
  ],
});

export const nfsGithubPullRequestsPlugin: FrontendPlugin =
  nativeGithubPullRequestsPlugin.withOverrides({
    extensions: [
      nativeGithubPullRequestsPlugin.getExtension('entity-content:github-pull-requests').override({
        params: {
          title: 'Pull Requests',
          filter: (entity: Entity) =>
            isFullComponent(entity) && isGithubPullRequestsAvailable(entity),
          loader: () =>
            import('@roadiehq/backstage-plugin-github-pull-requests').then(m => (
              <WithGitHubPermission>
                <m.Router />
              </WithGitHubPermission>
            )),
        },
      }),
    ],
  });

export const nfsGithubActionsPlugin: FrontendPlugin = nativeGithubActionsPlugin.withOverrides({
  extensions: [
    nativeGithubActionsPlugin.getExtension('entity-content:github-actions').override({
      params: {
        path: '/ci-cd',
        title: 'CI/CD',
        filter: (entity: Entity) => isFullComponent(entity) && isGithubActionsAvailable(entity),
        loader: () =>
          import('@backstage-community/plugin-github-actions').then(m =>
            compatWrapper(
              <WithGitHubPermission>
                <m.Router />
              </WithGitHubPermission>,
            ),
          ),
      },
    }),
  ],
});

export const nfsCodeInsightsPlugin: FrontendPlugin = nativeCodeInsightsPlugin.withOverrides({
  extensions: [
    nativeCodeInsightsPlugin.getExtension('entity-card:code-insights/contributors').override({
      params: {
        type: 'info',
        filter: (entity: Entity) => isFullComponent(entity) && isGithubInsightsAvailable(entity),
      },
    }),
  ],
});
